import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { suiteDisplayName, localizeAssignmentTitle } from '@/lib/i18nTitles';
import { useParams, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, FileText, Calendar, User as UserIcon, Building2, Lock, CheckCircle2, GraduationCap, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { assignmentStateReason, clientOutcomeLabel } from '@/lib/assignmentState';
import { ResponsesAuditDialog } from '@/components/admin/ResponsesAuditDialog';
import { useApp } from '@/context/AppContext';

export default function AdminEvaluationDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useApp();
  // Iter 41.8 — hm_admin (Super Admin) has full unrestricted access on
  // every assignment detail: edit Final Recommendation, change status,
  // view + download reports. hm_advisor keeps their original access.
  const canEdit = authUser?.role === 'hm_advisor' || authUser?.role === 'hm_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMeta, setSavedMeta] = useState(null);
  const [showResponses, setShowResponses] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await adminApi.getAssignment(id);
      setData(d);
      setText(d?.final_recommendation?.text || '');
      setSavedMeta(d?.final_recommendation || null);
    } catch (e) {
      toast.error(t('eval.detail.loadError', { defaultValue: 'Failed to load evaluation' }));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (id) reload(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminApi.saveFinalRecommendation(id, text);
      setSavedMeta(res);
      toast.success(t('eval.detail.recSaved', { defaultValue: 'Final recommendation saved' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const fmtDateTime = (s) => s ? new Date(s).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  // Prompt 21 — seed a training plan from this evaluation, then route into TalentTrain.
  const [seeding, setSeeding] = useState(false);
  const createTrainingPlan = async () => {
    setSeeding(true);
    try {
      const res = await adminApi.ttSeedFromEvaluation(data.company.id, id);
      toast.success(t('eval.detail.tpSeeded', {
        defaultValue: 'Training plan ready · {{n}} gaps loaded',
        n: res.snapshot_count,
      }));
      navigate(`/admin/talenttrain/${res.user_id}?seed=${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    } finally { setSeeding(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground" data-testid="eval-detail-loading">{t('common.loading')}</div>;
  if (!data) return null;

  const a = data.assignment;
  const c = data.candidate;
  const co = data.company;
  const r = data.report;
  const dirty = (text || '') !== (savedMeta?.text || '');
  const evaluationCompleted = !!r && ['under_review', 'edited_by_hm', 'approved', 'released_to_client'].includes(r.status);

  // Founder UX sprint — visual lifecycle (real state machine, labels only).
  const isES = (i18n.language || 'es').startsWith('es');
  const STEPS = [
    isES ? 'Borrador' : 'Draft',
    isES ? 'Invitación enviada' : 'Invitation sent',
    isES ? 'Participante en curso' : 'Participant started',
    isES ? 'Evaluación completada' : 'Completed',
    isES ? 'Informe en revisión' : 'Report under review',
    isES ? 'Entregado al cliente' : 'Delivered to client',
  ];
  let stepIdx = { draft: 0, requested: 1, in_progress: 2, submitted: 3, under_review: 4, completed: 4, released: 5 }[a?.status] ?? 0;
  if (a?.submitted_at && stepIdx < 3) stepIdx = 3;
  if (r?.status === 'released_to_client') stepIdx = 5;

  // P0 UAT 2026-08-12 — explicit state PRECEDENCE: later lifecycle dimensions
  // (report approval/delivery, client decision, process closure) override the
  // earlier assignment status. "Report in preparation" must never appear
  // after approval, delivery, decision or closure.
  const outcomeLbl = clientOutcomeLabel(a?.client_outcome, isES);
  const concluded = !!a?.process_closed;
  let lifecycleOverride = null;
  if (concluded) {
    lifecycleOverride = (isES
      ? 'Proceso concluido. '
      : 'Process concluded. ')
      + (outcomeLbl ? (isES ? `Decisión del cliente: ${outcomeLbl}. ` : `Client decision: ${outcomeLbl}. `) : '')
      + (isES ? 'No hay acciones pendientes.' : 'No pending actions remain.');
  } else if (r?.status === 'released_to_client') {
    lifecycleOverride = outcomeLbl
      ? (isES ? `Informe entregado al cliente. Decisión registrada: ${outcomeLbl}.` : `Report delivered to the client. Decision recorded: ${outcomeLbl}.`)
      : (isES ? 'Informe entregado al cliente. A la espera de la decisión del cliente.' : 'Report delivered to the client. Awaiting the client decision.');
  } else if (r?.status === 'approved') {
    lifecycleOverride = isES
      ? 'Informe aprobado — pendiente de entrega al cliente.'
      : 'Report approved — pending delivery to the client.';
  }

  const nextStep = lifecycleOverride || {
    draft: isES ? 'Completa la configuración para enviar la invitación al participante.' : 'Finish the setup to send the invitation to the participant.',
    requested: isES ? 'Invitación enviada. El sistema espera a que el participante inicie la evaluación.' : 'Invitation sent. The system is waiting for the participant to start.',
    in_progress: isES ? 'El participante está respondiendo. Espera a que complete la evaluación.' : 'The participant is answering. Wait until they complete the evaluation.',
    submitted: a?.suite_code === 'climara'
      ? (isES ? 'El participante envió sus respuestas. El avance y el reporte ejecutivo se gestionan a nivel de campaña en la consola Climara.' : 'The participant submitted their responses. Progress and the executive report are managed at campaign level in the Climara console.')
      : (isES ? 'El participante envió sus respuestas. El informe se prepara según el flujo de la suite.' : 'The participant submitted their responses. The report is prepared according to the suite workflow.'),
    under_review: isES ? 'Evaluación completada. Revisa el informe y registra la recomendación final.' : 'Evaluation completed. Review the report and record the final recommendation.',
    completed: isES ? 'Evaluación completada. Revisa el informe y registra la recomendación final.' : 'Evaluation completed. Review the report and record the final recommendation.',
    released: isES ? 'Informe publicado al cliente. No se requieren más acciones.' : 'Report released to the client. No further action required.',
  }[a?.status] || (isES ? 'Consulta el estado actual en el resumen operativo.' : 'Check the current state in the operational summary.');

  // Activity log — only real timestamps, chronological.
  const events = [];
  const pushEv = (at, label) => { if (at) events.push({ at, label }); };
  pushEv(a?.created_at, isES ? 'Evaluación creada y asignada' : 'Evaluation created and assigned');
  pushEv(a?.consent?.accepted_at, isES ? 'El participante aceptó el consentimiento informado' : 'Participant accepted the informed consent');
  pushEv(a?.last_reminder_at, isES
    ? `Recordatorio enviado${(a?.reminder_count || 0) > 1 ? ` (×${a.reminder_count})` : ''}`
    : `Reminder sent${(a?.reminder_count || 0) > 1 ? ` (×${a.reminder_count})` : ''}`);
  pushEv(a?.submitted_at, isES ? 'El participante completó la evaluación' : 'Participant completed the evaluation');
  pushEv(r?.generated_at || r?.created_at, isES ? 'Informe generado' : 'Report generated');
  pushEv(r?.approved_at, isES ? 'Informe aprobado' : 'Report approved');
  pushEv(r?.released_at, isES ? 'Informe entregado al cliente' : 'Report delivered to the client');
  pushEv(savedMeta?.updated_at, isES ? 'Recomendación final actualizada' : 'Final recommendation updated');
  // P0 UAT 2026-08-12 — complete cross-module lifecycle (derived from the
  // EXISTING recruitment relationship — no retroactive audit records).
  if (a?.client_outcome_at) {
    const lbl = clientOutcomeLabel(a?.client_outcome, isES);
    pushEv(a.client_outcome_at, isES ? `Decisión del cliente registrada${lbl ? `: ${lbl}` : ''}` : `Client decision recorded${lbl ? `: ${lbl}` : ''}`);
  }
  pushEv(a?.process_closed_at, isES ? 'Proceso de reclutamiento cerrado' : 'Recruitment process closed');
  events.sort((x, y) => String(x.at).localeCompare(String(y.at)));

  const invitationSent = a?.status && a.status !== 'draft';
  const started = a?.status === 'in_progress' || (a?.progress || 0) > 0 || !!a?.submitted_at;
  const completedFlag = !!a?.submitted_at || ['under_review', 'completed', 'released'].includes(a?.status);
  const yesNo = (v) => (v ? (isES ? 'Sí' : 'Yes') : (isES ? 'No' : 'No'));

  return (
    <>
      <Topbar
        title={c?.name ? `${c.name}` : (a?.title || t('eval.detail.title', { defaultValue: 'Evaluation' }))}
        subtitle={`${localizeAssignmentTitle(a?.title, i18n.language) || ''} · ${co?.name || ''}`}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/evaluations')}
            data-testid="eval-detail-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />{t('eval.detail.back', { defaultValue: 'Back to Evaluations' })}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Founder UX sprint — lifecycle stepper + dynamic next step */}
        <Card className="p-5 shadow-card" data-testid="eval-lifecycle-card">
          <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="eval-lifecycle-stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-1.5 shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium
                                 ${i < stepIdx ? 'bg-success/10 border-success/30 text-success'
                                   : i === stepIdx ? 'bg-primary/10 border-primary/40 text-primary'
                                   : 'bg-muted/30 border-muted text-muted-foreground'}`}
                     data-testid={`eval-step-${i}${i === stepIdx ? '-current' : ''}`}>
                  {i < stepIdx && <CheckCircle2 className="h-3 w-3" />}
                  {s}
                </div>
                {i < STEPS.length - 1 && <div className={`h-px w-4 shrink-0 ${i < stepIdx ? 'bg-success/50' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-3 flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="eval-next-step">
            <p className="text-xs leading-relaxed">
              <span className="font-semibold text-primary">{concluded ? (isES ? 'Estado final: ' : 'Final state: ') : (isES ? 'Próximo paso: ' : 'Next step: ')}</span>
              {nextStep}
            </p>
          </div>
        </Card>

        {/* Prompt 21 — Create Training Plan from this evaluation.
            P0 UAT 2026-08-12 — TalentTrain is for employees: hidden for
            external candidates and for rejected/closed candidacies (backend
            enforces the same eligibility server-side). */}
        {evaluationCompleted && canEdit
          && a?.candidate_type !== 'external'
          && !['not_selected', 'cancelled'].includes(a?.client_outcome) && (
          <Card className="p-4 shadow-card border-l-4 border-l-emerald-500 flex items-center gap-3 flex-wrap" data-testid="eval-create-tp-card">
            <GraduationCap className="h-5 w-5 text-emerald-600" />
            <div className="flex-1 min-w-[180px]">
              <p className="font-display font-bold text-sm">
                {t('eval.detail.tpHeading')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('eval.detail.tpHint')}
              </p>
            </div>
            <Button onClick={createTrainingPlan} disabled={seeding} className="bg-gradient-primary" data-testid="eval-create-tp-btn">
              <GraduationCap className="h-4 w-4 mr-1.5" />
              {seeding
                ? t('common.loading')
                : t('eval.detail.tpCta')}
            </Button>
          </Card>
        )}

        {/* Identity — operational summary */}
        <Card className="p-6 shadow-card" data-testid="eval-identity">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <Field label={t('eval.detail.candidate', { defaultValue: 'Evaluatee' })} value={c?.name} icon={UserIcon} />
            <Field
              label={isES ? 'Clasificación' : 'Classification'}
              value={a?.candidate_type === 'external'
                ? (isES ? 'Candidata externa' : 'External candidate')
                : (isES ? 'Colaborador' : 'Employee')}
            />
            <Field label={t('eval.detail.invitationEmail', { defaultValue: isES ? 'Correo de invitación' : 'Invitation email' })} value={c?.email || '—'} />
            <Field label={t('eval.detail.company', { defaultValue: 'Company' })} value={co?.name} icon={Building2} />
            <Field label={t('eval.detail.position', { defaultValue: 'Position' })} value={a?.position || '—'} />
            <Field label={isES ? 'Cuestionario asignado' : 'Assigned questionnaire'} value={localizeAssignmentTitle(a?.title, i18n.language) || '—'} icon={FileText} />
            <Field label={t('eval.detail.suite', { defaultValue: 'Suite' })} value={suiteDisplayName(a?.suite_code)} />
            <Field label={isES ? 'Fecha de asignación' : 'Assignment date'} value={a?.created_at ? new Date(a.created_at).toLocaleDateString(i18n.language) : '—'} icon={Calendar} />
            <Field label={t('eval.detail.deadline', { defaultValue: 'Deadline' })} value={a?.deadline ? new Date(a.deadline).toLocaleDateString(i18n.language) : '—'} icon={Calendar} />
            <Field label={t('eval.detail.status', { defaultValue: 'Status' })} value={<StatusBadge status={a?.status} />} />
            <Field label={isES ? 'Estado del informe' : 'Report status'} value={r?.status ? <StatusBadge status={r.status} /> : (isES ? 'Sin generar' : 'Not generated')} />
            <Field label={isES ? 'Invitación' : 'Invitation'} value={invitationSent ? (isES ? 'Enviada' : 'Sent') : (isES ? 'Sin enviar (borrador)' : 'Not sent (draft)')} />
            <Field label={isES ? 'Iniciada' : 'Started'} value={yesNo(started)} />
            <Field label={isES ? 'Completada' : 'Completed'} value={yesNo(completedFlag)} />
          </div>
          {assignmentStateReason(a, i18n.language?.startsWith('es'), { reportStatus: r?.status }) && (
            <p className="mt-3 text-xs text-muted-foreground border-l-2 border-primary/40 pl-2" data-testid="eval-detail-state-reason">
              {assignmentStateReason(a, i18n.language?.startsWith('es'), { reportStatus: r?.status })}
            </p>
          )}
          {canEdit && a?.status === 'submitted' && (
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={() => setShowResponses(true)}
                      data-testid="eval-view-responses-btn" className="gap-1.5">
                <ListChecks className="h-3.5 w-3.5" />
                {isES ? 'Ver respuestas' : 'View responses'}
              </Button>
            </div>
          )}
          {r && (
            <>
            {/* CTH-TC-078 — score transparency for consultants */}
            {r.scores?.overall_score != null && r.fit?.fit_score != null && (
              <p className="mt-3 text-[11px] text-muted-foreground border rounded-md bg-muted/30 p-2.5" data-testid="eval-score-explainer">
                {isES
                  ? `Puntaje global (${r.scores.overall_score}/100): resultado agregado del cuestionario según el modelo de puntuación aprobado del instrumento. Puntaje de idoneidad (${r.fit.fit_score}/100): análisis determinístico de ajuste al puesto (metodología role-fit) que compara el perfil medido con los requisitos del cargo. Son medidas distintas por diseño y pueden diferir; ninguna reemplaza a la otra.`
                  : `Overall score (${r.scores.overall_score}/100): aggregate questionnaire result per the instrument's approved scoring model. Suitability score (${r.fit.fit_score}/100): deterministic role-fit analysis comparing the measured profile against the position requirements. They are different measures by design and may differ; neither replaces the other.`}
              </p>
            )}
            <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-3 text-xs">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">{t('eval.detail.reportStatus')}: <StatusBadge status={r.status} className="ml-0.5" /></span>
              {r.scores?.overall_score != null && <span className="text-muted-foreground">· {t('eval.detail.score')} <span className="font-bold tabular-nums text-foreground">{r.scores.overall_score}</span></span>}
              {r.fit?.fit_score != null && <span className="text-muted-foreground">· {t('eval.detail.fit')} <span className="font-bold tabular-nums text-foreground">{r.fit.fit_score}</span></span>}
              {canEdit && (
                <>
                  <span className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin/reports/${r.id}`)}
                    data-testid="eval-view-report-btn"
                    className="gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {t('eval.detail.viewReport')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const blob = await adminApi.exportReport(r.id, 'pdf');
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `report_${r.id}.pdf`;
                        link.click();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || t('eval.detail.downloadFailed'));
                      }
                    }}
                    data-testid="eval-download-pdf-btn"
                    className="gap-1.5"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    {t('eval.detail.downloadPdf')}
                  </Button>
                </>
              )}
            </div>
            </>
          )}
        </Card>

        {/* Founder UX sprint — activity log (real timestamps only) */}
        <Card className="p-6 shadow-card" data-testid="eval-activity-log">
          <h3 className="font-display font-bold text-lg tracking-tight mb-1">
            {isES ? 'Actividad' : 'Activity'}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            {isES ? 'Registro cronológico de los eventos de esta evaluación.' : 'Chronological log of this evaluation’s events.'}
          </p>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">{isES ? 'Sin actividad registrada aún.' : 'No activity recorded yet.'}</p>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-4">
              {events.map((ev, i) => (
                <li key={`${ev.at}-${i}`} className="ml-4" data-testid={`eval-activity-item-${i}`}>
                  <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-primary/60 border-2 border-background" />
                  <p className="text-xs text-muted-foreground tabular-nums">{fmtDateTime(ev.at)}</p>
                  <p className="text-sm font-medium">{ev.label}</p>
                </li>
              ))}
            </ol>
          )}
        </Card>

        {/* Final Recommendation (Prompt 10) — compact; only when applicable */}
        {(evaluationCompleted || savedMeta?.text) ? (
        <Card className="p-6 shadow-card" data-testid="eval-final-rec-card">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                {t('eval.detail.finalRec')}
                {!canEdit && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
              </h3>
              <p className="text-sm text-muted-foreground">
                {canEdit
                  ? t('eval.detail.finalRecSubEditable')
                  : t('eval.detail.finalRecSubReadonly')}
              </p>
            </div>
            {savedMeta?.updated_at && (
              <div className="text-xs text-muted-foreground text-right" data-testid="eval-final-rec-meta">
                <div className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-success" />{t('eval.detail.savedAt')} {fmtDateTime(savedMeta.updated_at)}</div>
                {savedMeta.updated_by_name && <div>{t('eval.detail.savedBy')}&nbsp;{savedMeta.updated_by_name}</div>}
              </div>
            )}
          </div>

          {!canEdit && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2 px-3 py-2 rounded border bg-muted/30" data-testid="eval-final-rec-locked">
              <Lock className="h-3.5 w-3.5" />
              <span>{t('eval.detail.editRestricted')}</span>
            </div>
          )}

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('eval.detail.recPh')}
            rows={5}
            disabled={!canEdit}
            data-testid="eval-final-rec-textarea"
            className="resize-y min-h-[110px]"
          />

          <div className="flex items-center justify-end gap-2 mt-3">
            {dirty && canEdit && (
              <span className="text-xs text-muted-foreground italic" data-testid="eval-final-rec-dirty">
                {t('eval.detail.unsaved')}
              </span>
            )}
            {canEdit && (
              <Button onClick={save} disabled={saving || !dirty} data-testid="eval-final-rec-save">
                <Save className="h-4 w-4 mr-1.5" />
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            )}
          </div>
        </Card>
        ) : (
          <p className="text-xs text-muted-foreground italic px-1" data-testid="eval-final-rec-pending">
            {isES
              ? 'La recomendación final estará disponible cuando la evaluación se complete y el informe sea generado.'
              : 'The final recommendation becomes available once the evaluation is completed and the report is generated.'}
          </p>
        )}
      </div>
      <ResponsesAuditDialog assignmentId={a?.id || id} open={showResponses} onOpenChange={setShowResponses} />
    </>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}{label}
      </div>
      <div className="text-sm font-medium mt-1">{value || '—'}</div>
    </div>
  );
}
